import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import Icon from './Icon';

/**
 * The end of an infinitely scrolled movement list: loads the next page as it nears the view,
 * shows that one is loading, offers a retry when it failed, and says when the list is complete.
 */
export function TransactionListFooter({
  loadedCount,
  totalCount,
  hasMore,
  isLoadingMore,
  loadMoreError,
  onLoadMore,
}: {
  loadedCount: number;
  totalCount: number;
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMoreError: string | null;
  onLoadMore: () => void;
}) {
  // A failed page is retried by the button only: re-firing on view would retry in a loop.
  const sentinelRef = useInfiniteScroll<HTMLDivElement>(onLoadMore, {
    enabled: hasMore && !isLoadingMore && !loadMoreError,
    itemCount: loadedCount,
  });

  if (loadedCount === 0) return null;

  const count = new Intl.NumberFormat('en-US').format(totalCount);
  return (
    <div
      ref={sentinelRef}
      className="px-6 py-4 border-t border-slate-200 dark:border-slate-800 flex items-center justify-center text-sm text-slate-500 dark:text-slate-400"
      data-testid="transaction-list-footer"
    >
      {isLoadingMore ? (
        <span className="flex items-center gap-2">
          <Icon name="progress_activity" className="animate-spin" />
          Loading more transactions...
        </span>
      ) : loadMoreError ? (
        <span className="flex items-center gap-3 text-red-600 dark:text-red-400">
          Couldn&apos;t load more transactions.
          <button
            onClick={onLoadMore}
            className="rounded-md h-8 px-3 text-sm font-medium bg-primary text-white hover:bg-primary/90 transition-colors"
          >
            Retry
          </button>
        </span>
      ) : hasMore ? (
        <span>
          Showing {new Intl.NumberFormat('en-US').format(loadedCount)} of {count} transactions
        </span>
      ) : (
        <span>
          {totalCount === 1 ? 'That is the only transaction.' : `That's all ${count} transactions.`}
        </span>
      )}
    </div>
  );
}
